import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TCustomQueryProps } from "@/common/type/hooks.types";

import { ITeam } from "../interfaces/team";
import {
  createTeam,
  deleteTeam,
  getAllTeamServiceTypes,
  getTeam,
  updateTeam,
} from "../services";

export const useGetTeamById = (id: string) => {
  return useQuery<ITeam, Error>({
    queryKey: ["getTeamById", id],
    queryFn: () => getTeam(id),
    enabled: !!id,
  });
};

export const useGetAllTeamServiceTypes = () => {
  return useQuery({
    queryKey: ["getAllTeamServiceTypes"],
    queryFn: () => getAllTeamServiceTypes(),
  });
};

export const useCreateTeam = ({
  handleSuccess,
}: Partial<TCustomQueryProps<ITeam>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (team: ITeam) => createTeam(team),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getTeamById"] });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when creating team:", error?.message);
    },
  });
};

export const useUpdateTeam = ({
  handleSuccess,
}: Partial<TCustomQueryProps<ITeam>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (team: ITeam) => updateTeam(team),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });
      queryClient.invalidateQueries({ queryKey: ["getTeamById", id] });

      handleSuccess?.(variables);
    },
    onError: (error: Error) => {
      console.error("Error when updating team:", error?.message);
    },
  });
};

export const useDeleteTeam = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps<ITeam>> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });

      handleSuccess?.();
    },
    onError: (error: Error) => {
      handleError?.();
      console.error("Error when deleting team:", error?.message);
    },
  });
};
